import { useEffect, useState } from "react"

import { generateHTML } from "@tiptap/html"
import StarterKit from "@tiptap/starter-kit"
import { atom, useAtom } from "jotai"
import { RESET, atomWithDefault } from "jotai/utils"
import { useRouter } from "next/router"
import { FormProvider, useForm } from "react-hook-form"
import { v4 as uuidv4 } from "uuid"

import Alert from "components/Alert"
import { ActionButton } from "components/Button"
import FaIcon from "components/FontAwesomeIcon"
import { Select } from "components/forms/FormElements"
import Href from "components/Href"
import { Container, Section } from "components/Layout"
import { Heading, Lead } from "components/Text"
import { productPagePropsAtom } from "pages/products/[slug]"
import { addToCartAtom } from "store"
import { formatAmountForDisplay } from "utils/stripe"

import Addon from "../Addon"
import ProductImages from "../ProductImages"

import Quantity from "./Quantity"

import type { ProductAddonTemplate, ProductAddonType } from "@prisma/client"
import type { ProductVariantWithImagesAndAddons } from "pages/products/[slug]"

type ProductTypeSimple = {
  variant: string
  quantity: number
  addons?: Record<
    string,
    {
      id: string
      title: string
      quantity: number
      type: ProductAddonType
      template: ProductAddonTemplate
    }
  >
}

// store the selected variant here. Allow to set this when change form state. Reset it when change product
const variantAtom = atomWithDefault(get => {
  const productPageProps = get(productPagePropsAtom)
  return productPageProps?.product.variants[0]
})

const setVariantAtom = atom(
  null,
  (get, set, variant: ProductVariantWithImagesAndAddons) => {
    set(variantAtom, variant)
  }
)

// generate addons from the selected variant and productPageProps
const addonsAtom = atomWithDefault(get => {
  const variant = get(variantAtom)
  const productPageProps = get(productPagePropsAtom)
  if (!productPageProps || !variant) return []
  return [...variant.addons, ...productPageProps.product.addons]
})

const ProductTypeSimple: React.FC = () => {
  const [productPageProps] = useAtom(productPagePropsAtom)
  const router = useRouter()
  const [, addToCart] = useAtom(addToCartAtom)
  const [addingToCart, setAddingToCart] = useState<boolean>(false)
  const [addedToCart, setAddedToCart] = useState<boolean>(false)
  const [variant, resetVariant] = useAtom(variantAtom)
  const [, setVariant] = useAtom(setVariantAtom)
  const [addons, resetAddons] = useAtom(addonsAtom)
  const isMoreThanOneVariant =
    productPageProps?.product.variants &&
    productPageProps.product.variants.length > 1

  const rhf = useForm<ProductTypeSimple>({
    defaultValues: {
      quantity: 1,
      ...(variant?.id && { variant: variant.id }),
      addons: {
        ...[productPageProps?.product.addons, variant?.addons]
          .flat()
          .reduce((acc, addon) => {
            if (!addon || addon.type === "TEMPLATE") {
              return acc
            }
            return {
              ...acc,
              [addon.id]: {
                id: addon.id,
                title: addon.title,
                quantity: 0,
                type: addon.type,
                template: addon.template
              }
            }
          }, {})
      }
    }
  })

  useEffect(() => {
    // when the variant atom changes, set the variant field in the form - Using rhf with jotai to get the benefits of it
    rhf.setValue("variant", variant?.id || "")

    // compare the id prop in the addonsAtom array with the rhf.getValue("addons") object keys. If not present, unregister the field
    addons.forEach(addon => {
      // if the addons.${addon.id}.id (extra id because quantity is registered separately in the addons) is not present in the form, set the value
      if (!rhf.getValues(`addons.${addon.id}.id`)) {
        rhf.setValue(`addons.${addon.id}`, {
          id: addon.id,
          title: addon.title,
          quantity: 0,
          type: addon.type,
          template: addon.template
        })
      }
    })

    const addonsInFormState = rhf.getValues("addons")
    // remove addons present in the form that are not in the addonsAtom array
    addonsInFormState &&
      Object.keys(addonsInFormState).forEach(addonId => {
        if (!addons.find(addon => addon.id === addonId)) {
          rhf.unregister(`addons.${addonId}`)
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, addons])

  useEffect(() => {
    // reset the atoms when the product changes
    resetVariant(RESET)
    resetAddons(RESET)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath])

  const submitForm = async () => {
    if (!productPageProps?.product.id) return
    setAddingToCart(true)
    const addonsInFormState = rhf.getValues("addons") || {}
    addToCart({
      id: uuidv4(),
      productId: productPageProps?.product?.id,
      productTitle: productPageProps?.product?.title,
      productType: "SIMPLE",
      variantId: variant?.id || "",
      ...(isMoreThanOneVariant && { variantTitle: variant?.title || "" }),
      ...(addonsInFormState && {
        addons: Object.values(addonsInFormState).map(addon => ({
          id: addon.id,
          title: addon.title,
          type: addon.type,
          template: addon.template,
          quantity: addon.quantity
        }))
      }),
      quantity: rhf.getValues("quantity")
    })
    // wait for a second as react is too fast for the UX sometimes!
    await new Promise(resolve => setTimeout(resolve, 1000)).then(() => {
      setAddingToCart(false)
      setAddedToCart(true)
    })
  }

  return (
    <FormProvider {...rhf}>
      <Section>
        <Container className="grid smmax:grid-cols-1 mdmin:grid-cols-2 gap-6">
          {/* Left */}
          <div>
            {productPageProps?.product.images && (
              <ProductImages
                selectedImageId={variant?.images?.[0].id}
                images={productPageProps.product.images}
                className="aspect-square"
              />
            )}
          </div>
          {/* Right */}
          <div>
            <Heading>{productPageProps?.product.title}</Heading>
            {productPageProps?.product.content && (
              <div
                dangerouslySetInnerHTML={{
                  __html: generateHTML(
                    JSON.parse(productPageProps.product.content),
                    [StarterKit]
                  )
                }}
              />
            )}

            {/* Price */}
            <Lead>{formatAmountForDisplay(variant?.price || 0)}</Lead>

            {isMoreThanOneVariant && (
              <div className="border p-3">
                <Lead>Choose an option</Lead>
                {/* Create a select from all the product variants */}
                <Select
                  options={
                    productPageProps?.product.variants.map(variant => ({
                      value: variant.title,
                      label: variant.title
                    })) || []
                  }
                  onChange={e => {
                    const selectedVariant =
                      productPageProps?.product.variants.find(
                        variant => variant.title === e.target.value
                      )
                    if (selectedVariant) setVariant(selectedVariant)
                  }}
                />

                {/* Display the content for this variant */}
                {variant?.content && (
                  <div
                    className="mt-3"
                    dangerouslySetInnerHTML={{
                      __html: generateHTML(JSON.parse(variant.content), [
                        StarterKit
                      ])
                    }}
                  />
                )}
              </div>
            )}

            {/* Addons */}
            {addons.length > 0 && (
              <div className="border p-3">
                <Lead>Additional options:</Lead>
                <div className="flex flex-col gap-4">
                  {addons.map(addon => (
                    <Addon key={addon.id} addon={addon} />
                  ))}
                </div>
              </div>
            )}

            {/* Quantity input */}
            <Quantity />

            <ActionButton
              isLoading={addingToCart}
              loadingText="Adding..."
              onClick={rhf.handleSubmit(submitForm)}
            >
              <FaIcon icon="cart-shopping" className="mr-2" />
              Add to cart
            </ActionButton>

            <Alert variant="success" show={addedToCart}>
              Added to cart.{" "}
              <Href
                href="/products"
                title="Products"
                className="underline"
                noUnderline
              >
                Continue shopping
              </Href>{" "}
              or{" "}
              <Href
                href="/checkout"
                title="Checkout"
                className="underline"
                noUnderline
              >
                go to checkout
              </Href>
            </Alert>
          </div>
        </Container>
      </Section>
    </FormProvider>
  )
}

export default ProductTypeSimple
